import { useDraggable } from '@dnd-kit/core';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { getLastEdit, type RevisionReusableContent } from '@gitbook/hive-core';
import { useHiveBranch } from '@gitbook/hive-react';
import {
    createPalette,
    DateShortRelative,
    TextMatchHighlight,
    usePaletteTrigger,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    confirm,
    DialogRoot,
    ListItem,
    ListItemBody,
    ListItemControls,
    ListItemDescription,
    ListItemDetail,
    ListItemIndicator,
    useDialogTriggerState,
} from '@gitbook/spine-react';
import { assertIsDefined } from '@gitbook/util-ts';

import { getDragItem } from '../DragAndDrop';
import { UserPrettyName } from '../Users';
import { RenameReusableContentDialog } from './RenameReusableContentDialog';

/**
 * Item in the list of reusable content in the content sidebar.
 */
export function ReusableContentItem(props: {
    space: api.Space;
    reusableContent: RevisionReusableContent;
    selected: boolean;
    onPress?: () => void;
    query?: string;
}) {
    const { space, reusableContent, selected, query = '', onPress } = props;

    const hiveBranch = useHiveBranch();
    const renameDialogTrigger = useDialogTriggerState();
    const lastEdit = getLastEdit(reusableContent.edits);

    const drag = useDraggable({
        disabled: false,
        ...getDragItem({
            type: 'reusable-content',
            id: reusableContent.id,
            previewLabel: reusableContent.title,
            previewIcon: 'ReusableContent',
        }),
    });

    const paletteTrigger = usePaletteTrigger({
        palette: () =>
            createPalette({
                groups: [
                    {
                        key: 'edit',
                        commands: [
                            {
                                icon: Icon.Edit,
                                key: 'file.edit',
                                title: 'Rename',
                                trigger: () => {
                                    renameDialogTrigger.open();
                                },
                            },
                            {
                                icon: Icon.Trashcan,
                                key: 'file.delete',
                                title: 'Delete',
                                trigger: () => {
                                    void confirm({
                                        title: 'Delete reusable content',
                                        description: (
                                            <>
                                                Are you sure you want to delete this reusable
                                                content? The content will be removed from all pages
                                                where it is used.
                                            </>
                                        ),
                                        kind: 'danger',
                                        confirmLabel: 'Delete',
                                        onConfirm: async () => {
                                            assertIsDefined(hiveBranch);

                                            hiveBranch.change((change) => {
                                                change.removeReusableContent(reusableContent.id);
                                            });

                                            analytics.logEvent(
                                                analytics.createSpaceEvent(space, {
                                                    type: 'reusable_content_delete',
                                                })
                                            );
                                        },
                                    });
                                },
                            },
                        ],
                    },
                ],
            }),
    });

    return (
        <>
            <DialogRoot {...renameDialogTrigger.rootProps}>
                <RenameReusableContentDialog
                    reusableContent={reusableContent}
                    onRenamed={() => {
                        analytics.logEvent(
                            analytics.createSpaceEvent(space, {
                                type: 'reusable_content_rename',
                                method: 'sidebar',
                            })
                        );
                    }}
                />
            </DialogRoot>
            <ListItem
                selected={selected || paletteTrigger.opened}
                onPress={onPress ?? paletteTrigger.triggerProps.onPress}
            >
                <ListItemIndicator>
                    <Button
                        ref={drag.setNodeRef}
                        kind="blank"
                        iconOnly
                        label="Drag this reusable content into the editor to insert"
                        leadingIcon={Icon.GrabberVertical}
                        className="cursor-grab"
                        {...drag.listeners}
                    />
                </ListItemIndicator>
                <ListItemBody>
                    <ListItemDescription>
                        <TextMatchHighlight query={query} text={reusableContent.title} />
                    </ListItemDescription>
                    <ListItemDetail>
                        {lastEdit ? (
                            <React.Suspense fallback={null}>
                                <UserPrettyName userId={lastEdit.user} />
                                {' edited '}
                                <DateShortRelative
                                    value={new Date(lastEdit.updatedAt)}
                                    onlyDate={false}
                                />
                            </React.Suspense>
                        ) : null}
                    </ListItemDetail>
                </ListItemBody>
                <ListItemControls>
                    <Button
                        {...paletteTrigger.triggerProps}
                        leadingIcon={Icon.MoreVertical}
                        label="More"
                        size="small"
                        kind="nested"
                        iconOnly
                    />
                </ListItemControls>
            </ListItem>
        </>
    );
}
